'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { AuthProvider, useAuth } from '@/components/AuthContext';
import CalculatorSettingsDialog from './CalculatorSettingsDialog';
import ProposalTemplate from './ProposalTemplate';
import { regionalizePath } from '@/lib/config/branches-config';
import { useLanguage } from '@/lib/config/useLanguage';
import {
  buildLeadThankYouUrl,
  clearPendingLeadSubmissionId,
  extractOptionalLeadId,
  getOrCreatePendingLeadSubmissionId,
} from '@/lib/forms/lead-thank-you';
import LeadGateModal, { type LeadContact } from './LeadGateModal';
import CityCombobox from './CityCombobox';
import Building3D, { type Building3DHandle } from './Building3D';
import { createElementPdfBlob, pdfBlobToBase64, downloadPdfBlob, pdfBlobFromBase64 } from './pdf';
import {
  BRAND,
  PRICING,
  RAL_COLORS,
  WIDTH_OPTIONS,
  LENGTH_OPTIONS,
  HEIGHT_OPTIONS,
  WINDOWS_MAX,
  GATES_MAX,
  DEFAULT_STATE,
  ROOF_LABELS,
  calcEstimate,
  buildEstimateLines,
  formatRub,
  getRal,
  type CalculatorState,
  type RoofType,
  type CalculatorSettings,
} from './calculator-config';

import { getLeadFormCity } from '@/lib/forms/region';
import { trackLeadFormStage, trackLeadFormSuccess, reachMetrikaGoal } from '@/lib/analytics/metrika';

const FORM_TYPE = 'calculator-commercial-offer';

/** Сегментированный переключатель из 2-3 вариантов. */
function Segmented<T extends string | boolean>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <div className="flex gap-1 rounded-xl border border-slate-200 bg-slate-50 p-1">
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={String(opt.value)}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`flex-1 rounded-lg px-2 py-2 text-sm font-medium transition-colors ${
              active
                ? 'bg-white text-[#214688] shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/** Простая параметрическая SVG-иллюстрация здания (вместо 3D на этапе 1). */
function BuildingIllustration({ state }: { state: CalculatorState }) {
  const ral = getRal(state.ral);
  const isDouble = state.roof === 'double';
  const wallColor = ral.hex;
  const roofColor = '#94a3b8';
  const stroke = '#475569';
  // показываем до 5 окон в ряд для наглядности
  const winCount = Math.min(state.windows, 5);

  return (
    <svg viewBox="0 0 320 200" className="h-auto w-full" role="img" aria-label="Схема здания">
      {/* небо/земля */}
      <rect x="0" y="0" width="320" height="160" fill="#eff6ff" />
      <rect x="0" y="150" width="320" height="50" fill="#dcfce7" />
      {/* корпус */}
      <rect x="60" y="90" width="200" height="70" fill={wallColor} stroke={stroke} strokeWidth="2" />
      {/* кровля */}
      {isDouble ? (
        <polygon points="50,90 160,52 270,90" fill={roofColor} stroke={stroke} strokeWidth="2" />
      ) : (
        <polygon points="50,90 270,68 270,90" fill={roofColor} stroke={stroke} strokeWidth="2" />
      )}
      {/* ворота */}
      {state.gates > 0 && (
        <rect x="142" y="120" width="36" height="40" fill="#64748b" stroke={stroke} strokeWidth="1.5" />
      )}
      {/* окна */}
      {Array.from({ length: winCount }).map((_, i) => (
        <rect
          key={i}
          x={72 + i * 24}
          y={100}
          width={14}
          height={14}
          fill="#bae6fd"
          stroke={stroke}
          strokeWidth="1"
        />
      ))}
    </svg>
  );
}

function CalculatorContent() {
  const router = useRouter();
  const pathname = usePathname() || '/';
  const regionSearch = useSearchParams().toString();
  const [state, setState] = useState<CalculatorState>(() => ({ ...DEFAULT_STATE, cityName: getLeadFormCity(pathname, regionSearch) }));
  const [modalOpen, setModalOpen] = useState(false);
  const { user, isLoading: authLoading } = useAuth();
  const canManage = !authLoading && user?.canManageCalculator === true;
  const canUsePdf = !authLoading && user?.canUseCalculatorPdf === true;
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState<CalculatorSettings>({ pricing: PRICING, revision: 0, updatedAt: null });
  const [settingsReady, setSettingsReady] = useState(false);
  const [settingsError, setSettingsError] = useState('');
  const [pdfError, setPdfError] = useState('');
  const [pdfBuilding, setPdfBuilding] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      const response = await fetch('/api/calculator/settings/', { cache: 'no-store' });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error('Не удалось загрузить актуальные цены.');
      setSettings((previous) => previous.revision === data.revision ? previous : data);
      setSettingsReady(true); setSettingsError('');
    } catch { setSettingsError('Не удалось загрузить актуальные цены. Повторите загрузку.'); }
  }, []);
  useEffect(() => {
    if (settingsOpen) return;
    void loadSettings();
    const onFocus = () => { void loadSettings(); };
    const interval = window.setInterval(onFocus, 60_000);
    window.addEventListener('focus', onFocus);
    return () => { window.clearInterval(interval); window.removeEventListener('focus', onFocus); };
  }, [loadSettings, settingsOpen]);
  useEffect(() => {
    setState((prev) => ({ ...prev, cityName: getLeadFormCity(pathname, regionSearch) }));
  }, [pathname, regionSearch]);

  const pendingProposal = useRef<{ key: string; blob: Blob; filename: string; contentBase64: string } | null>(null);
  const kpRef = useRef<HTMLDivElement>(null);
  const kp3dImgRef = useRef<HTMLImageElement>(null);
  const kpFallbackRef = useRef<HTMLDivElement>(null);
  const building3dRef = useRef<Building3DHandle>(null);
  const { localizePath, langCode } = useLanguage();
  const privacyPolicyUrl = regionalizePath(
    localizePath('/pages/politika-konfidentsialnosti-personalnykh-dannykh'),
    'spb',
  );

  const estimate = useMemo(() => calcEstimate(state, settings.pricing), [state, settings.pricing]);
  const estimateLines = useMemo(() => buildEstimateLines(state, estimate, settings.pricing), [state, estimate, settings.pricing]);
  const proposalVersion = JSON.stringify({ state, settings });
  const currentProposalVersion = useRef(proposalVersion);
  currentProposalVersion.current = proposalVersion;
  const ral = getRal(state.ral);

  const set = <K extends keyof CalculatorState>(key: K, value: CalculatorState[K]) =>
    setState((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    setPdfError('');
    if (kp3dImgRef.current) {
      kp3dImgRef.current.removeAttribute('src');
      kp3dImgRef.current.style.display = 'none';
    }
    if (kpFallbackRef.current) kpFallbackRef.current.style.display = 'block';
  }, [state, settings, canUsePdf]);

  const preparePdf = async () => {
    const shot = building3dRef.current?.capture();
    const image = kp3dImgRef.current;
    if (shot && image) {
      image.src = shot; image.style.display = 'block';
      if (kpFallbackRef.current) kpFallbackRef.current.style.display = 'none';
      await image.decode().catch(() => undefined);
    }
    if (!kpRef.current) throw new Error('Шаблон КП пока не готов. Повторите попытку.');
    return createElementPdfBlob(kpRef.current);
  };

  const handleStaffPdf = async () => {
    if (pdfBuilding) return;
    setPdfBuilding(true); setPdfError('');
    try {
      // Prices are public; check the ordinary session before bypassing the lead form.
      const authResponse = await fetch('/api/auth/check/', { cache: 'no-store' });
      const auth = await authResponse.json();
      if (!authResponse.ok) throw new Error('Не удалось проверить аккаунт. Повторите попытку.');
      if (!auth.authenticated) throw new Error('Сессия завершилась. Войдите в аккаунт ещё раз.');
      if (auth.user?.canUseCalculatorPdf !== true) throw new Error('У аккаунта нет доступа к PDF без заявки.');
      const pricesResponse = await fetch('/api/calculator/settings/', { cache: 'no-store' });
      const current = await pricesResponse.json();
      if (!pricesResponse.ok || !current.success) throw new Error('Не удалось загрузить актуальные цены.');
      if (current.revision !== settings.revision) {
        setSettings(current);
        throw new Error('Цены обновились. Проверьте новый расчёт и нажмите «Сгенерировать PDF» ещё раз.');
      }
      if (currentProposalVersion.current !== proposalVersion) throw new Error('Параметры изменились. Нажмите «Сгенерировать PDF» ещё раз.');
      const blob = await preparePdf();
      if (currentProposalVersion.current !== proposalVersion) throw new Error('Параметры изменились. Нажмите «Сгенерировать PDF» ещё раз.');
      downloadPdfBlob(blob, `KP-${state.width}x${state.length}x${state.height}-${state.cityName}.pdf`);
    } catch (cause) {
      setPdfError(cause instanceof Error ? cause.message : 'Не удалось сгенерировать PDF.');
    } finally { setPdfBuilding(false); }
  };

  const handleConfirm = async (
    contact: LeadContact,
    smartToken: string,
  ): Promise<{ ok: boolean; error?: string }> => {
    trackLeadFormStage(FORM_TYPE, 'attempt');
    try {
      const submissionId = getOrCreatePendingLeadSubmissionId(FORM_TYPE, window.location.pathname);
      const proposalKey = JSON.stringify({ state, pricing: settings.pricing, revision: settings.revision });
      if (pendingProposal.current?.key !== proposalKey) {
        const blob = await preparePdf();
        if (blob.size > 5 * 1024 * 1024) throw new Error('PDF превышает допустимый размер 5 МБ.');
        pendingProposal.current = {
          key: proposalKey, blob,
          filename: `KP-${state.width}x${state.length}x${state.height}-${state.cityName}.pdf`,
          contentBase64: await pdfBlobToBase64(blob),
        };
      }
      const proposal = pendingProposal.current;

      const response = await fetch('/api/calculator', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: contact.name,
          phone: contact.phone,
          email: contact.email,
          privacy: contact.privacy,
          marketing: contact.marketing,
          formType: FORM_TYPE,
          config: {
            city: state.cityName,
            width: state.width,
            length: state.length,
            height: state.height,
            roof: state.roof,
            warm: state.warm,
            crane: state.crane,
            windows: state.windows,
            gates: state.gates,
            ral: state.ral,
          },
          estimate,
          pricingRevision: settings.revision,
          proposal: { filename: proposal.filename, contentBase64: proposal.contentBase64 },
          pageUrl: typeof window !== 'undefined' ? window.location.href : '',
          locale: langCode,
          smartToken,
          submissionId,
        }),
      });

      const data = await response.json().catch(() => ({ success: false }));
      if (!response.ok || !data.success) {
        void loadSettings();
        trackLeadFormStage(FORM_TYPE, 'error', 'server');
        return { ok: false, error: data.message || 'Ошибка при отправке заявки.' };
      }

      trackLeadFormSuccess(FORM_TYPE, submissionId);

      // Download exactly the uploaded bytes. A replay uses the original saved PDF.
      try {
        const saved = data.proposal;
        downloadPdfBlob(saved ? pdfBlobFromBase64(saved.contentBase64) : proposal.blob, saved?.filename || proposal.filename);
        reachMetrikaGoal('calculator-pdf-success');
      } catch {
        reachMetrikaGoal('calculator-pdf-error');
      }
      pendingProposal.current = null;

      const thankYouUrl = buildLeadThankYouUrl({
        pathname: window.location.pathname,
        search: window.location.search,
        formType: FORM_TYPE,
        leadId: extractOptionalLeadId(data),
        sid: submissionId,
      });

      clearPendingLeadSubmissionId(FORM_TYPE, window.location.pathname);
      router.push(thankYouUrl);
      return { ok: true };
    } catch (cause) {
      trackLeadFormStage(FORM_TYPE, 'error', 'network-or-captcha');
      return { ok: false, error: cause instanceof Error && !(cause instanceof TypeError) ? cause.message : 'Не удалось отправить заявку. Попробуйте ещё раз.' };
    }
  };

  return (
    <section id="calculator" className="mt-10 [container-type:inline-size] md:mt-14">
      <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm">
        {/* Шапка */}
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 md:px-8">
          <div>
            <div className="text-lg font-bold tracking-tight text-[#214688] md:text-xl">
              {BRAND.name}
            </div>
            <div className="text-xs text-slate-500">Калькулятор металлоконструкций</div>
          </div>
          {canManage && <button type="button" disabled={!settingsReady} onClick={() => setSettingsOpen(true)} className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-[#214688] disabled:opacity-50">Настройки</button>}
        </div>

        {settingsError && <div role="alert" className="mx-5 mt-4 rounded-xl bg-red-50 p-3 text-sm text-red-700">
          {settingsError} <button type="button" className="underline" onClick={() => void loadSettings()}>Повторить</button>
        </div>}

        {/* Колонки по ширине самого блока, а не экрана: в каталоге он стоит рядом с сайдбаром. */}
        <div className="grid gap-6 p-5 md:p-8 [@container(760px<=width<1180px)]:grid-cols-2 [@container(min-width:1180px)]:grid-cols-[1fr_1fr_0.9fr]">
          {/* ── Параметры ── */}
          <div className="space-y-5">
            <div>
              <Label htmlFor="calc-city" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Город строительства
              </Label>
              <CityCombobox
                id="calc-city"
                value={state.cityName}
                onChange={(cityName) => set('cityName', cityName)}
              />
            </div>

            <div>
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Габариты (Ш × Д × В), м
              </span>
              <div className="mt-1.5 grid grid-cols-3 gap-2">
                <Select value={String(state.width)} onValueChange={(v) => set('width', Number(v))}>
                  <SelectTrigger className="h-11 rounded-xl border-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WIDTH_OPTIONS.map((w) => (
                      <SelectItem key={w} value={String(w)}>{w} м</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={String(state.length)} onValueChange={(v) => set('length', Number(v))}>
                  <SelectTrigger className="h-11 rounded-xl border-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LENGTH_OPTIONS.map((l) => (
                      <SelectItem key={l} value={String(l)}>{l} м</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={String(state.height)} onValueChange={(v) => set('height', Number(v))}>
                  <SelectTrigger className="h-11 rounded-xl border-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HEIGHT_OPTIONS.map((h) => (
                      <SelectItem key={h} value={String(h)}>{h} м</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Кровля</span>
                <div className="mt-1.5">
                  <Segmented<RoofType>
                    value={state.roof}
                    onChange={(v) => set('roof', v)}
                    options={[
                      { value: 'single', label: 'Односкатная' },
                      { value: 'double', label: 'Двускатная' },
                    ]}
                  />
                </div>
              </div>
              <div>
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Исполнение</span>
                <div className="mt-1.5">
                  <Segmented<boolean>
                    value={state.warm}
                    onChange={(v) => set('warm', v)}
                    options={[
                      { value: false, label: 'Холодное' },
                      { value: true, label: 'Тёплое' },
                    ]}
                  />
                </div>
              </div>
            </div>

            <div>
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Кран-балка (3,2 т)</span>
              <div className="mt-1.5">
                <Segmented<boolean>
                  value={state.crane}
                  onChange={(v) => set('crane', v)}
                  options={[
                    { value: false, label: 'Нет' },
                    { value: true, label: 'Да (усиление)' },
                  ]}
                />
              </div>
            </div>

            <div>
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Цвет (RAL)</span>
              <div className="mt-2 flex flex-wrap gap-2">
                {RAL_COLORS.map((c) => {
                  const active = c.ral === state.ral;
                  return (
                    <button
                      key={c.ral}
                      type="button"
                      title={`RAL ${c.ral} — ${c.name}`}
                      onClick={() => set('ral', c.ral)}
                      className={`h-8 w-8 rounded-full border-2 transition ${
                        active ? 'border-[#214688] ring-2 ring-[#214688]/30' : 'border-slate-200'
                      }`}
                      style={{ backgroundColor: c.hex }}
                    />
                  );
                })}
              </div>
            </div>

            <div>
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Кол-во окон (шт)</span>
              <div className="mt-2 grid max-h-44 grid-cols-6 gap-1.5 overflow-y-auto pr-1">
                {Array.from({ length: WINDOWS_MAX + 1 }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => set('windows', i)}
                    className={`rounded-lg border py-2 text-sm font-medium transition ${
                      i === state.windows
                        ? 'border-[#214688] bg-[#214688] text-white'
                        : 'border-slate-200 text-slate-600 hover:border-[#214688]/40'
                    }`}
                  >
                    {i}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Кол-во ворот (шт)</span>
              <div className="mt-2 grid grid-cols-6 gap-1.5">
                {Array.from({ length: GATES_MAX + 1 }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => set('gates', i)}
                    className={`rounded-lg border py-2 text-sm font-medium transition ${
                      i === state.gates
                        ? 'border-[#214688] bg-[#214688] text-white'
                        : 'border-slate-200 text-slate-600 hover:border-[#214688]/40'
                    }`}
                  >
                    {i}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* ── Характеристики + визуализация ── */}
          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Характеристики
              </div>
              <div className="mt-1 text-lg font-bold text-[#214688]">{state.cityName}</div>
              <div className="mt-1 text-sm text-slate-600">
                {state.width}×{state.length}×{state.height} м · {ROOF_LABELS[state.roof]}
              </div>
              <div className="mt-1 text-sm text-slate-600">
                {state.warm ? 'Тёплое (сэндвич)' : 'Холодное (профлист)'} · RAL {ral.ral} {ral.name}
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-3">
              <Building3D ref={building3dRef} state={state} fallback={<BuildingIllustration state={state} />} />
            </div>
          </div>

          {/* ── Смета ── */}
          <div className="[@container(760px<=width<1180px)]:col-span-2 [@container(min-width:1180px)]:sticky [@container(min-width:1180px)]:top-24 [@container(min-width:1180px)]:col-span-1 [@container(min-width:1180px)]:self-start">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Смета проекта
              </div>
              <dl className="mt-4 space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <dt className="text-slate-600">Металлокаркас</dt>
                  <dd className="font-semibold text-slate-900">{formatRub(estimate.steel)}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-slate-600">Ограждающие</dt>
                  <dd className="font-semibold text-slate-900">{formatRub(estimate.wall)}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-slate-600">Окна и Ворота</dt>
                  <dd className="font-semibold text-slate-900">{formatRub(estimate.ops)}</dd>
                </div>
              </dl>
              <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3 text-xs text-slate-500">
                <span>Снеговой: <b className="text-slate-700">{estimate.snow}</b></span>
                <span>Ветровой: <b className="text-slate-700">{estimate.wind}</b></span>
              </div>
              <div className="mt-3 text-right text-3xl font-extrabold text-[#214688]">
                {formatRub(estimate.total)}
              </div>
              <button
                type="button"
                disabled={!settingsReady || authLoading || pdfBuilding}
                onClick={() => {
                  if (canUsePdf) { void handleStaffPdf(); }
                  else { trackLeadFormStage(FORM_TYPE, 'open'); setModalOpen(true); }
                }}
                aria-describedby="calculator-kp-step"
                className="mt-5 w-full rounded-xl bg-[#214688] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#1a3870] disabled:opacity-50"
              >
                {!settingsReady ? 'ЗАГРУЖАЕМ ЦЕНЫ…' : pdfBuilding ? 'ГЕНЕРИРУЕМ PDF…' : canUsePdf ? 'СГЕНЕРИРОВАТЬ PDF' : 'ПОЛУЧИТЬ КП (PDF)'}
              </button>
              <p id="calculator-kp-step" className="mt-3 text-center text-xs leading-5 text-slate-600">
                {canUsePdf ? 'Предпросмотр КП — ниже. PDF создаётся и скачивается по нажатию кнопки.' : 'На следующем шаге потребуется телефон. PDF скачается сразу после отправки формы.'}
              </p>
              <p className="mt-2 text-center text-xs leading-5 text-slate-400">
                Расчёт предварительный. Точную стоимость рассчитывает инженерный отдел.
              </p>
            </div>
          </div>
        </div>
      </div>

      {canManage && <CalculatorSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} settings={settings} onSaved={setSettings} />}

      {canUsePdf && <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-4">
        <h2 className="text-lg font-semibold text-[#214688]">Предпросмотр КП</h2>
        <p className="mt-2 text-sm text-slate-500">Макет обновляется при изменении параметров. PDF создаётся только по нажатию кнопки.</p>
        {pdfError && <p role="alert" className="mt-3 text-sm text-red-700">{pdfError}</p>}
        <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-slate-50 p-3">
          <ProposalTemplate ref={kpRef} state={state} estimate={estimate} estimateLines={estimateLines}
            imageRef={kp3dImgRef} fallbackRef={kpFallbackRef} illustration={<BuildingIllustration state={state} />} />
        </div>
      </div>}

      <LeadGateModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        onConfirm={handleConfirm}
        privacyPolicyUrl={privacyPolicyUrl}
      />

      {/* Guests use the same template off screen; employees see its live HTML layout. */}
      {!canUsePdf && <div className="pointer-events-none fixed -left-[9999px] top-0" aria-hidden="true">
        <ProposalTemplate ref={kpRef} state={state} estimate={estimate} estimateLines={estimateLines}
          imageRef={kp3dImgRef} fallbackRef={kpFallbackRef} illustration={<BuildingIllustration state={state} />} />
      </div>}
    </section>
  );
}

export default function MetalBuildingCalculator() {
  return <AuthProvider><CalculatorContent /></AuthProvider>;
}
